import Menu from 'antd/es/menu';
import { Icon } from 'components/Icon';
import { Text } from 'components/Typography';
import { FontFamilies, Screens } from 'core/CssVariables';
import styled, { ThemeProps } from 'styled-components';
import { TThemeProps } from 'theme/theme';

const { ItemGroup } = Menu;

/* ZEST-757 side panel: a flat rail of 44px rows, each an 8px-radius pill,
   grouped under section headings. The active row is signalled three ways —
   fill, accent icon and a leading rail — because the fill alone is a ~1.1:1
   tint and can't carry "you are here" on its own. */
export const SMenu = styled(Menu)`
  && {
    transition: width 0.3s cubic-bezier(0.2, 0, 0, 1) 0s;
    border: 0;
    background-color: ${props => props.theme.sidebar.backgroundColor};
    font-family: ${FontFamilies.FontShell};

    /* The icomoon glyphs paint a solid fill; the redesign's nav icons are
       stroked outlines that take currentColor. Colour them separately so the
       outlines never get flooded with the fill colour. */
    svg:not(.nav-icon) {
      fill: ${props => props.theme.sidebar.iconColor};
      transition: fill 0.2s ease;
    }

    .nav-icon {
      color: ${props => props.theme.sidebar.iconColor};
      transition: color 0.2s ease;
      flex: 0 0 auto;
    }

    li {
      background-color: transparent;

      &:after {
        content: none;
      }

      /* Figma draws the submenu chevron in the label ink (#19193A), a step
         darker than the item icons (#64748B) — they are not one grey. */
      .ant-menu-submenu-arrow {
        color: ${props => props.theme.sidebar.color};
      }

      .ant-menu-title-content {
        user-select: none;
      }
    }

    a {
      text-decoration: none;
      color: inherit;

      &:hover {
        color: inherit;
      }
    }
  }

  &.ant-menu-root {
    background-color: ${props => props.theme.sidebar.backgroundColor};
    padding: 12px 0;

    /* Section heading — WORK / CLIENTS / MANAGE / SETTINGS */
    .ant-menu-item-group-title {
      padding: 4px 24px;
      /* 11px, not the component's 12px: measured against the design screenshot,
         "WORK" is 38.7px wide there against 42px at 12px — the headings sit a
         step smaller than the component spec says. */
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.1em;
      line-height: 16px;
      color: ${props => props.theme.sidebar.groupColor};
    }

    /* The design gives the first heading a step more contrast than the rest and
       repeats that in both themes — #334155 over #64748B on light, #E2E8F0 over
       #CBD5E1 on dark — so it reads as deliberate emphasis on the leading
       section rather than a slip. */
    .ant-menu-item-group.is-lead-group > .ant-menu-item-group-title {
      color: ${props => props.theme.sidebar.groupLeadColor};
    }

    .ant-menu-item-group + .ant-menu-item-group {
      margin-top: 10px;
    }

    /* Rows: menu items and submenu parents share one geometry. */
    .ant-menu-item,
    .ant-menu-submenu-title {
      height: 44px;
      line-height: 24px;
      margin: 0 8px;
      padding: 10px 12px !important;
      width: auto;
      border-radius: 8px;
      display: flex;
      align-items: center;
      color: ${props => props.theme.sidebar.color};
      font-size: 14px;
      font-weight: 500;
      transition: background-color 0.2s ease, color 0.2s ease;

      .ant-menu-title-content {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: 0;
        padding-left: 0;
        overflow: hidden;

        /* Rows without a submenu keep their icon and label inside the tooltip
           wrapper, so the row's own gap can't reach them — lay that wrapper
           out as the row instead. */
        > span {
          display: flex;
          align-items: center;
          /* 10px, not the design's 8px: the icon library's own artwork sits
             anywhere from 0.8px to 3.3px inside its 20px box, so a flat 8px
             leaves the edge-filling icons visibly tighter than the rest. */
          gap: 10px;
          min-width: 0;
          overflow: hidden;
        }

        /* A flex item defaults to min-width:auto, so the link refuses to
           shrink below its text and the clipping happens above it — leaving
           the label itself reporting no overflow. Let it shrink so the
           ellipsis, and NavLabel's measurement, land on the label. */
        a {
          display: block;
          min-width: 0;
        }

        /* The ellipsis sits on the label itself rather than on a wrapper:
           NavLabel compares its own scrollWidth to clientWidth to decide
           whether the text needs a tooltip, and a wrapper clipping on its
           behalf would leave the label reporting no overflow at all. */
        .nav-label {
          display: block;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          /* Positioned so the label itself is the hit target. Left static, the
             surrounding link answers the hover instead, the label's own
             mouseenter never fires and the tooltip stays shut. */
          position: relative;
        }
      }

      &:hover {
        background-color: ${props => props.theme.sidebar.hoverBackgroundColor};
      }
    }

    /* Rows that use antd's own icon slot need the gap on the slot, not on the
       row's flex gap. Two shapes to cover: antd tags the icon
       .ant-menu-item-icon for a submenu child, but a submenu parent's icon
       arrives as a bare svg (the empty tooltip wrapper collapses away), so
       match the icon class too or the parents end up flush against the label. */
    /* Match the icon by element, not by class. Three shapes reach this slot and
       they carry different classes: antd tags some .ant-menu-item-icon, the
       redesign's outlines carry .nav-icon, and rows still falling back to the
       icomoon set carry only .icon — those last ones were left flush against
       their label when this keyed off classes alone. */
    .ant-menu-submenu-title > svg + .ant-menu-title-content,
    .ant-menu-item > svg + .ant-menu-title-content,
    .ant-menu-submenu-title > .ant-menu-item-icon + .ant-menu-title-content,
    .ant-menu-item > .ant-menu-item-icon + .ant-menu-title-content {
      margin-left: 10px;
    }

    /* Selected row */
    .ant-menu-item-selected,
    .ant-menu-submenu-selected > .ant-menu-submenu-title {
      position: relative;
      background-color: ${props =>
        props.theme.sidebar.activeBackgroundColor} !important;
      color: ${props => props.theme.sidebar.activeColor};
      font-weight: 600;

      svg:not(.nav-icon) {
        fill: ${props => props.theme.sidebar.activeIconColor};
      }

      .nav-icon {
        color: ${props => props.theme.sidebar.activeIconColor};
      }

      /* antd paints the selected submenu's arrow with its LESS primary
         colour, which is the brand blue rather than the sidebar's own ink — so
         the selected row's chevron has to be restated to match its label. */
      .ant-menu-submenu-arrow {
        color: ${props => props.theme.sidebar.activeColor};
      }

      /* Sits inside the pill: antd clips menu items, so a rail hung off the
         row's outer edge would never be drawn. Driven by a token so the dark
         theme — whose #334155 fill is legible unaided, and whose design draws
         no rail — can switch it off. */
      &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 8px;
        bottom: 8px;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: ${props => props.theme.sidebar.activeRailColor};
      }
    }

    /* Expanded submenu: children sit inside the parent's column, indented so
       the hierarchy reads without a second background plane. */
    &.ant-menu-inline:not(.is-collapsed) .ant-menu-sub {
      background-color: transparent;

      .ant-menu-item {
        padding-left: 40px !important;
      }
    }

    /* In the 64px rail there is no column to indent into — an open submenu's
       icons would sit out of line with every top-level icon. */
    &.is-collapsed .ant-menu-sub {
      background-color: transparent;

      .ant-menu-item {
        padding: 10px !important;
        justify-content: center;
      }
    }

    /* Collapsed rail: icons only, centred, headings hidden. */
    &.is-collapsed {
      .ant-menu-item-group-title {
        display: none;
      }

      .ant-menu-item,
      .ant-menu-submenu-title {
        justify-content: center;
        padding: 10px !important;

        .ant-menu-title-content {
          justify-content: center;
          gap: 0;
        }
      }

      .ant-menu-submenu-arrow {
        display: none;
      }
    }
  }
`;

export const SMenuItemGroup = styled(ItemGroup)``;

export const SSidebarLogo = styled.div<{ collapsed?: boolean }>`
  display: flex;
  /* The rail drops the logo entirely and centres the control on its own, which
     is what the design's collapsed variant shows — it clips the wordmark out of
     a 15px-wide frame rather than stacking it above the control. Dropping it
     also lines the rail's header up with the panel's: both are one 24px row in
     16px padding, so the nav below does not shift when the panel toggles. */
  justify-content: ${props => (props.collapsed ? 'center' : 'space-between')};
  align-items: center;
  gap: 16px;
  padding: ${props => (props.collapsed ? '16px 0' : '16px 24px')};
  background: ${props => props.theme.sidebar.backgroundColor};
  border-bottom: 1px solid ${props => props.theme.sidebar.borderColor};

  .icon-logo {
    height: 24px;
    width: auto;
    cursor: pointer;
    color: ${props => props.theme.sidebar.logoColor};
    flex: 0 0 auto;
  }

  @media (max-width: ${Screens.ScreensMD}) {
    justify-content: start;
  }
`;

export const SSidebarExpandIcon = styled(Icon)`
  fill: ${({ theme }: ThemeProps<TThemeProps>) => theme.sidebar.controlColor};
  cursor: pointer;
  border-radius: 18px;
  @media (max-width: ${Screens.ScreensMD}) {
    visibility: hidden;
  }
`;

/* The panel control is the design's own sidebar glyph — a panel outline with a
   chevron — not the icomoon double chevron. It is stroked rather than filled, so
   it takes `color` and the artwork draws in currentColor, following the control
   token in both themes. */
export const SSidebarControl = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }: ThemeProps<TThemeProps>) => theme.sidebar.controlColor};
  cursor: pointer;
  flex: 0 0 auto;
  @media (max-width: ${Screens.ScreensMD}) {
    visibility: hidden;
  }
`;

/* The design closes the panel with a rule above the version, mirroring the one
   under the logo: footer Container carries a 1px #E2E8F0 stroke on its top edge
   only (per-side top:1, others 0). */
export const SVersionBar = styled.div`
  margin-top: auto;
  border-top: 1px solid ${props => props.theme.sidebar.borderColor};
`;

export const SVersionLink = styled(Text)`
  color: ${({ theme }: ThemeProps<TThemeProps>) => theme.sidebar.versionColor};
  font-family: ${FontFamilies.FontShell};
  cursor: pointer;
  padding: 12px 24px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;
